import { useState, useEffect, useRef, useCallback } from 'react';
import * as repo from '@/supabase/deposit/repository/onboardingRepository';
import type { OnboardingSession, LatestOnboardingResult, SanitizedVerificationAttempt, LatestVerificationResult } from '@/supabase/deposit/types/onboarding';
import type { DepositBankCode } from '@/supabase/deposit/banks';
import { getDepositBank } from '@/supabase/deposit/banks';
import { logError } from '@/lib/sentry';

const POLL_MS = 1500;

export type WizardPhase =
  | 'input'         // 계좌번호 입력
  | 'starting'      // start_onboarding 진행
  | 'awaiting_code' // 세션 생성, 인증코드 대기(안내 표시)
  | 'code_received' // 인증코드 라이브 표시 + 카운트다운
  | 'completing'    // complete_onboarding 진행
  | 'completed'     // provisional 성공 화면(apick 미실행/degraded 폴백)
  | 'verifying'        // 1원 발송됨, 입금 통지 확인 중(~60s)
  | 'verify_confirmed' // 1원 매칭 → 검증 완료(강한 완주감)
  | 'verify_failed'    // 미도착/실패/불명 → 트러블슈팅 + 재검증/확인대기
  | 'session_expired' // 인증번호 도착 전 15분 대기시간 종료(컨테이너가 모달 자동 닫기)
  | 'code_expired'  // 코드 만료(트러블슈팅/재시도)
  | 'gateway_busy'  // 타 셀러 점유 친절 거절
  | 'error';        // 기타 실패

export interface UseDepositOnboardingResult {
  phase: WizardPhase;
  session: OnboardingSession | null;
  authCode: string | null;
  codeRemainingSec: number | null;  // code_received 동안만 non-null
  verifyRemainingSec: number | null; // verifying 중 카운트다운(ceil), 그 외 null
  registered: boolean;              // 등록확인 SMS 도착(소프트 신호)
  busyUntil: Date | null;           // gateway_busy 남은시간
  errorMessage: string | null;
  start: (bankCode: DepositBankCode, accountNumber: string) => Promise<void>;
  complete: () => Promise<void>;
  cancel: () => Promise<void>;
  resume: () => Promise<void>;       // 모달 open 시 기존 세션 복구(멀티탭/새로고침)
  reset: () => void;                 // 처음으로
}

export function useDepositOnboarding(): UseDepositOnboardingResult {
  const [phase, setPhase] = useState<WizardPhase>('input');
  const [session, setSession] = useState<OnboardingSession | null>(null);
  const [busyUntil, setBusyUntil] = useState<Date | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tick, setTick] = useState(0); // 카운트다운 리렌더 트리거
  const [attempt, setAttempt] = useState<SanitizedVerificationAttempt | null>(null); // 검증 attempt(sanitized)
  const offsetRef = useRef(0);         // 서버시간 - 클라시간(ms)
  // 코드 폴링 effect가 deps 없이 최신 session을 읽기 위한 미러(authType 게이트용, 안정 참조).
  const sessionRef = useRef<OnboardingSession | null>(null);
  // 비동기 complete()의 세대 가드(aliveRef의 reset/cancel 버전): reset/cancel이 증가시키면
  // 진행 중 complete()의 늦은 resolve가 stale phase를 세팅하지 못한다(모달 닫고 재오픈 race).
  const genRef = useRef(0);

  // 폴링/카운트다운 도중 unmount 후 setState 방지.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // 폴링 결과를 phase/session으로 환원(transient 'starting'/'completing'은 폴링이 안 돌므로 안전).
  const applyLatest = useCallback((res: LatestOnboardingResult) => {
    if (!aliveRef.current) return;
    const serverNowMs = Date.parse(res.server_now);
    offsetRef.current = serverNowMs - Date.now();
    const s = res.session;
    setSession(s);
    sessionRef.current = s;
    if (!s) { setPhase('input'); return; }
    if (s.status === 'completed') {
      // 계좌가 삭제된 완료 세션(account 링크 null)은 성공화면 대신 입력 단계로 —
      // 안 그러면 resume 시 죽은 계좌의 '확인 대기' 화면이 떠 재연동을 막는다.
      if (s.seller_deposit_account_id == null) { setSession(null); setPhase('input'); return; }
      setPhase('completed');
      return;
    }
    if (s.status === 'code_received') {
      const remaining = s.code_expires_at
        ? Date.parse(s.code_expires_at) - (Date.now() + offsetRef.current)
        : 1;
      setPhase(remaining > 0 ? 'code_received' : 'code_expired');
      return;
    }
    if (s.status === 'awaiting_code') {
      const leaseExpiresMs = Date.parse(s.lease_expires_at);
      if (Number.isFinite(serverNowMs) && Number.isFinite(leaseExpiresMs)
          && leaseExpiresMs <= serverNowMs) {
        setPhase('session_expired');
        return;
      }
      setPhase('awaiting_code');
      return;
    }
    // expired/failed/draining → P0는 처음으로(재시도 유도).
    setPhase('input');
    setSession(null);
  }, []);

  // 폴링: awaiting_code / code_received 동안만.
  useEffect(() => {
    if (phase !== 'awaiting_code' && phase !== 'code_received') return;
    // 하나(Type B, authType='instant') 등은 인증코드 SMS가 오지 않아 awaiting_code에서
    // 코드-대기 폴링이 무의미(셀러가 등록 후 완료 버튼을 눌러야 진행) → 스킵.
    // verifying 검증 폴링은 별도 effect라 영향 없음. Type A(code)는 조건이 항상 false라 동작 불변.
    if (phase === 'awaiting_code'
        && sessionRef.current
        && getDepositBank(sessionRef.current.bank_code)?.authType === 'instant') {
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await repo.getLatestOnboarding();
        if (!cancelled) applyLatest(res);
      } catch (err) {
        logError('useDepositOnboarding.poll', err); // 일시 실패는 다음 틱에 재시도
      }
    };
    const id = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [phase, applyLatest]);

  // 검증 폴링: verifying 중 attempt sanitized 폴(confirmed/failed/expired 파생). 만료는 server-owned.
  useEffect(() => {
    if (phase !== 'verifying' || !session) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await repo.getLatestVerification(session.id);
        if (cancelled || !aliveRef.current) return;
        offsetRef.current = Date.parse(res.server_now) - Date.now();
        const att = res.attempt;
        setAttempt(att);
        if (!att) return;
        if (att.status === 'confirmed') setPhase('verify_confirmed');
        else if (att.status === 'expired' || att.status === 'expired_late'
                 || att.status === 'failed' || att.status === 'send_unknown') setPhase('verify_failed');
        // 'sent'/'sending' → 계속 폴링(서버가 confirmed/expired로 수렴)
      } catch (err) {
        logError('useDepositOnboarding.verifyPoll', err); // 일시 실패는 다음 틱에 재시도
      }
    };
    const id = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [phase, session]);

  // 카운트다운: code_received / verifying 동안 1초 틱. (만료 전이는 폴링/서버 권위.)
  useEffect(() => {
    if (phase !== 'code_received' && phase !== 'verifying') return;
    const id = setInterval(() => {
      if (aliveRef.current) setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(id);
  }, [phase]);

  // 실제 남은 ms(서버시간 앵커 보정). 만료 판정의 기준 — floor로 인한 최대 1초 조기만료 방지.
  const codeRemainingMs =
    phase === 'code_received' && session?.code_expires_at
      ? Date.parse(session.code_expires_at) - (Date.now() + offsetRef.current)
      : null;
  // 표시용 초: ceil로 마지막 0.x초 동안 "1초"로 보여주고, 실제 0이 될 때만 0.
  const codeRemainingSec =
    codeRemainingMs != null ? Math.max(0, Math.ceil(codeRemainingMs / 1000)) : null;

  // verifying 남은 ms/초(서버시간 앵커 보정). deadline_at null(sending)이면 카운트다운 없음 —
  // 폴링이 finalize 후 deadline을 채운다. 만료 전이는 폴링/서버 권위라 별도 auto-transition 불필요.
  const verifyRemainingMs =
    phase === 'verifying' && attempt?.deadline_at
      ? Date.parse(attempt.deadline_at) - (Date.now() + offsetRef.current)
      : null;
  const verifyRemainingSec =
    verifyRemainingMs != null ? Math.max(0, Math.ceil(verifyRemainingMs / 1000)) : null;

  // 만료 전이: 표시 초가 아니라 실제 남은 ms(<=0) 기준(tick 의존으로 재평가).
  useEffect(() => {
    if (phase === 'code_received' && codeRemainingMs != null && codeRemainingMs <= 0) setPhase('code_expired');
  }, [phase, codeRemainingMs, tick]);

  const start = useCallback(async (bankCode: DepositBankCode, accountNumber: string) => {
    setErrorMessage(null); setBusyUntil(null);
    setPhase('starting');
    try {
      await repo.startOnboarding(bankCode, accountNumber);
      const res = await repo.getLatestOnboarding(); // 서버시간 앵커 + 최신 상태
      applyLatest(res);
    } catch (err) {
      if (!aliveRef.current) return; // unmount 후 setState 방지(complete와 대칭)
      const kind = repo.parseOnboardingError((err as Error)?.message ?? '');
      if (kind.kind === 'gateway_busy') { setBusyUntil(kind.until); setPhase('gateway_busy'); return; }
      if (kind.kind === 'active_session_exists') {
        setErrorMessage('진행 중인 다른 계좌 연동이 있어요. 취소하고 다시 시도해주세요.');
      } else if (kind.kind === 'account_owned_by_other') {
        setErrorMessage('이미 다른 계정에 등록된 계좌예요.');
      } else if (kind.kind === 'unsupported_bank') {
        setErrorMessage('선택한 은행은 아직 연동을 준비하고 있어요.');
      } else if (kind.kind === 'not_allowlisted') {
        setErrorMessage('해당 은행 연동은 아직 준비 중이에요. 곧 열릴 예정이에요.');
      } else {
        setErrorMessage('연동을 시작하지 못했어요. 잠시 후 다시 시도해주세요.');
        logError('useDepositOnboarding.start', err);
      }
      setPhase('error');
    }
  }, [applyLatest]);

  const complete = useCallback(async () => {
    if (!session) return;
    const sessionId = session.id;
    const gen = ++genRef.current;   // 이 실행의 세대. reset/cancel가 이후 증가시키면 늦은 resolve 무시.
    setErrorMessage(null);
    setPhase('completing');
    try {
      const res = await repo.completeAndVerify(sessionId);
      if (!aliveRef.current || gen !== genRef.current) return;
      const att = res.attempt;
      // 백엔드가 세션을 completed로 승격(account_id 바인딩) — 로컬 세션 동기화(cancel 가드/정합).
      // completeAndVerify는 세션을 반환하지 않으므로 별도 조회. 실패는 비치명(폴링/resume이 보정).
      try {
        const latest = await repo.getLatestOnboarding();
        if (!aliveRef.current || gen !== genRef.current) return;
        offsetRef.current = Date.parse(latest.server_now) - Date.now();
        if (latest.session) setSession(latest.session);
      } catch (serr) {
        logError('useDepositOnboarding.completeSync', serr);
      }
      setAttempt(att);
      if (att && (att.status === 'sent' || att.status === 'sending')) {
        // 송금 진행/완료 → 입금 통지 대기. deadline_at은 서버 기준이며 폴링이 server_now로 재anchor.
        setPhase('verifying');
      } else if (att && att.status === 'confirmed') {
        setPhase('verify_confirmed');
      } else if (att) {
        // send_unknown/failed/expired/expired_late → 송금 시도했으나 미수렴. 트러블슈팅.
        setPhase('verify_failed');
      } else {
        // attempt null = degraded(cap/cooldown로 apick 스킵). 계좌는 이미 provisional → 확인대기 폴백.
        setPhase('completed');
      }
    } catch (err) {
      if (!aliveRef.current || gen !== genRef.current) return; // unmount/취소·리셋 후 setState 방지
      const kind = repo.parseOnboardingError((err as Error)?.message ?? '');
      if (kind.kind === 'session_expired') { setPhase('session_expired'); return; }
      if (kind.kind === 'not_ready') setErrorMessage('아직 인증번호가 도착하지 않았어요.');
      else if (kind.kind === 'account_bank_mismatch') setErrorMessage('기존에 등록된 은행과 달라요. 계좌를 제거한 뒤 다시 등록해주세요.');
      else if (kind.kind === 'account_owned_by_other') setErrorMessage('이미 다른 계정에 등록된 계좌예요.');
      else { setErrorMessage('완료 처리에 실패했어요. 다시 시도해주세요.'); logError('useDepositOnboarding.complete', err); }
      setPhase('error');
    }
  }, [session]);

  const cancel = useCallback(async () => {
    genRef.current++;   // 진행 중 complete()의 늦은 resolve 무효화
    const s = session;
    setSession(null);
    setPhase('input');
    setErrorMessage(null);
    setBusyUntil(null);
    if (s && ['awaiting_code', 'code_received', 'draining'].includes(s.status)) {
      try { await repo.cancelOnboarding(s.id); }
      catch (err) { logError('useDepositOnboarding.cancel', err); } // best-effort, lease TTL이 회수
    }
  }, [session]);

  const resume = useCallback(async () => {
    const gen = ++genRef.current;   // 이 resume의 세대. reset/cancel(=닫기→재오픈)이 증가시키면 stale resume의 늦은 write 무시.
    try {
      const res = await repo.getLatestOnboarding();
      if (!aliveRef.current || gen !== genRef.current) return; // 닫힘/재오픈 후 applyLatest로 clobber 방지
      applyLatest(res);
      // 완료 세션(provisional 계좌)이면 진행 중/완료된 검증 attempt 복원(멀티탭/새로고침).
      const s = res.session;
      if (s?.status === 'completed' && s.seller_deposit_account_id != null) {
        // 검증 읽기 일시 실패(read fail)와 '시도 없음'(attempt null)을 혼동하면 안 됨 —
        // 일시 실패를 'completed'로 떨구면 멀쩡한 verifying 카운트다운/실패 트러블슈팅을 '확인대기'로
        // 가려버린다(같은 모달 세션엔 verifying 폴링이 없어 재오픈 전까지 자가복구 안 됨). 짧게 재시도.
        let v: LatestVerificationResult | null = null;
        for (let i = 0; i < 3 && v === null; i++) {
          try {
            v = await repo.getLatestVerification(s.id);
          } catch (verr) {
            if (i === 2) logError('useDepositOnboarding.resumeVerify', verr); // 3회 실패 → 'completed' 폴백 유지
            else await new Promise((r) => setTimeout(r, 300));
          }
        }
        if (!aliveRef.current || gen !== genRef.current) return;
        if (v) {
          offsetRef.current = Date.parse(v.server_now) - Date.now();
          setAttempt(v.attempt);
          const st = v.attempt?.status;
          if (st === 'confirmed') setPhase('verify_confirmed');
          else if (st === 'sent' || st === 'sending') setPhase('verifying');
          else if (v.attempt) setPhase('verify_failed'); // send_unknown/failed/expired/expired_late
          // v.attempt null(읽기 성공) → applyLatest의 'completed'(apick 미실행 폴백) 유지
        }
        // v null(3회 실패) → applyLatest의 'completed' 폴백 유지(최후 수단, 세션 자체는 유효)
      }
    } catch (err) {
      if (!aliveRef.current || gen !== genRef.current) return; // unmount/닫힘 후 setState 방지(complete와 대칭)
      logError('useDepositOnboarding.resume', err);
      setPhase('input');
    }
  }, [applyLatest]);

  const reset = useCallback(() => {
    genRef.current++;   // 진행 중 complete()의 늦은 resolve 무효화(모달 닫기→재오픈 race)
    setSession(null);
    setPhase('input');
    setErrorMessage(null);
    setBusyUntil(null);
    setAttempt(null);
  }, []);

  return {
    phase,
    session,
    authCode: session?.auth_code ?? null,
    codeRemainingSec,
    verifyRemainingSec,
    registered: !!session?.registered_confirmed_at,
    busyUntil,
    errorMessage,
    start, complete, cancel, resume, reset,
  };
}
